import AddLocationOutlinedIcon from '@mui/icons-material/AddLocationOutlined';
import { FormControl, Grid, InputAdornment, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import FindAnotherOffice from './FindAnotherOffice';
import DistanceIndicator from '@src/components/DateAndTime/Location/DistanceIndicator';
import { Application, OfficeSearchApiResponse } from '@src/lib/__generated/api';
import { useGetFacilityLocations } from '@src/services/useGetFacilityLocations';
import { useFacilityStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
export default function LocationSelect() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const facility = useFacilityStore(store => store.facility);
  const setFacility = useFacilityStore(store => store.setFacility);
  const router = useRouter();
  const facilityCodeFromURL = router.isReady ? new URLSearchParams(router.asPath.split('?')[1]).get('fc') : null;
  const digitalVisitReason = useReasonForVisitStore(store => store.reasonForVisit);
  const longitude = usePatientInformationFormStore(store => store.geoLocation.longitude);
  const latitude = usePatientInformationFormStore(store => store.geoLocation.latitude);
  const patientAge = usePatientInformationFormStore(store => store.patientAge());
  const {
    t
  } = useTranslation('dateAndTime');
  const facilityParams = {
    digitalVisitReason,
    longitude,
    latitude,
    application: Application.AspenDental,
    ...(patientAge && {
      patientAge
    })
  };
  const {
    data: facilityLocations
  } = useGetFacilityLocations(facilityParams, true);
  useEffect(() => {
    if (!facilityLocations || !router.isReady) return;
    if (facilityCodeFromURL) {
      // fc already in URL (e.g. deep link) — populate store from matched location, no router update
      const match = facilityLocations.find(l => l.facilityCode === facilityCodeFromURL);
      setFacility(match ?? facilityLocations[0]);
    } else {
      // No fc in URL — populate store only; URL is written only on explicit user selection to avoid GTM double-fire
      setFacility(facilityLocations[0]);
    }
  }, [facilityLocations, facilityCodeFromURL, router.isReady, setFacility]);
  const selectFacility = (selected: OfficeSearchApiResponse) => {
    setFacility(selected);
    router.replace({
      pathname: router.asPath.split('?')[0],
      query: {
        ...router.query,
        fc: selected.facilityCode
      }
    }, undefined, {
      shallow: true
    });
  };
  const handleChange = (event: SelectChangeEvent) => {
    if (event.target?.value) {
      selectFacility(facilityLocations?.find(location => location.facilityCode === event.target.value) as OfficeSearchApiResponse);
    } else {
      setOpenDrawer(true);
    }
  };
  return <FormControl fullWidth data-sentry-element="FormControl" data-sentry-component="LocationSelect" data-sentry-source-file="LocationSelect.tsx">
			<InputLabel id="location-label" data-sentry-element="InputLabel" data-sentry-source-file="LocationSelect.tsx">{t('location')}</InputLabel>
			<Select labelId="location-label" startAdornment={<InputAdornment position="start">
						<AddLocationOutlinedIcon />
					</InputAdornment>} value={facility?.facilityCode || ''} onChange={handleChange} renderValue={value => facilityLocations?.find(location => location.facilityCode === value)?.title} label="Location" data-sentry-element="Select" data-sentry-source-file="LocationSelect.tsx">
				{facilityLocations?.slice(0, 5).map(location => {
        return <MenuItem key={location.facilityCode} value={location.facilityCode ?? ''} sx={{
          lineHeight: 'normal',
          py: 2,
          px: 4
        }}>
							<ListItemText disableTypography primary={<Grid container justifyContent="space-between">
										<Typography variant="bodySmallBook">{location.title}</Typography>
										<DistanceIndicator distance={location.distance} variant="bodySmallBook" />
									</Grid>} secondary={<Grid container>
										<Grid item xs={12}>
											<Typography variant="bodySmallBook">{location.address?.line1}</Typography>
										</Grid>
										<Grid item xs={12}>
											<Typography variant="bodySmallBook">{t('alignersAvailable')}</Typography>
										</Grid>
									</Grid>} />
						</MenuItem>;
      })}
				<MenuItem sx={{
        display: 'flex',
        justifyContent: 'center'
      }} data-sentry-element="MenuItem" data-sentry-source-file="LocationSelect.tsx">
					<Typography color="secondary" variant="buttonLabel" data-sentry-element="Typography" data-sentry-source-file="LocationSelect.tsx">
						{t('findAnotherOffice')}
					</Typography>
				</MenuItem>
			</Select>
			<FindAnotherOffice openDrawer={openDrawer} setOpenDrawer={setOpenDrawer} facilityLocations={facilityLocations} setFacility={selectFacility} data-sentry-element="FindAnotherOffice" data-sentry-source-file="LocationSelect.tsx" />
		</FormControl>;
}